import { Check } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useApi } from "../hooks";
import { ErrorState } from "./Ui";
import type { JarvisSettings as Settings } from "../jarvis";

/** Settings → Jarvis: which AI Jarvis runs on. The other one answers if the picked one fails. */
export default function JarvisSettings({ canEdit }: { canEdit: boolean }) {
  const settings = useApi<Settings>("/api/jarvis/settings");
  const [saving, setSaving] = useState("");
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  if (settings.error) return <ErrorState message={settings.error} onRetry={settings.reload} />;
  const data = settings.data;

  async function pick(provider: Settings["provider"]) {
    if (!canEdit || saving || provider === data?.provider) return;
    setSaving(provider); setNotice(null);
    try {
      await api<Settings>("/api/jarvis/settings", { method: "PUT", body: JSON.stringify({ provider }) });
      settings.reload();
      setNotice({ tone: "success", text: `Jarvis now runs on ${provider === "claude" ? "Claude" : "OpenAI"}.` });
    } catch (e) {
      setNotice({ tone: "danger", text: e instanceof Error ? e.message : "That did not save." });
    } finally { setSaving(""); }
  }

  return (
    <section className="appearance-settings jarvis-settings" aria-label="Jarvis settings">
      <fieldset className="appearance-group appearance-control-group" disabled={!canEdit || !data}>
        <legend>Jarvis runs on</legend>
        <div className="appearance-choices">
          {(data?.choices || []).map((c) => {
            const selected = data?.provider === c.id;
            return (
              <label key={c.id} className={`appearance-choice ${selected ? "selected" : ""}`}>
                <input type="radio" name="jarvis-provider" value={c.id} checked={selected} onChange={() => void pick(c.id)} />
                <span>
                  <strong>{saving === c.id ? "Saving…" : c.label}</strong>
                  <small>{c.connected ? `${c.model}, through ${c.via}` : "Not connected"}</small>
                </span>
                {selected && <Check aria-hidden="true" />}
              </label>
            );
          })}
        </div>
      </fieldset>
      {data && data.choices.some((c) => !c.connected) && (
        <p className="muted">To use {data.choices.filter((c) => !c.connected).map((c) => c.label).join(" or ")}, add its key in <Link to="/settings#integrations">Integrations</Link>.</p>
      )}
      <p className="muted">If the one you pick does not answer, the other one does, and the reply says so.{canEdit ? "" : " Only an owner can change this."}</p>
      {notice && <p className={notice.tone === "danger" ? "ads-ai-proposal-error" : "workflow-note"} role="status">{notice.text}</p>}
    </section>
  );
}
